'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, Goal, H, Lesson, M, MB, NumberQuiz, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/numbers-extra'

const BalanceScale = dynamic(() => import('../_components/widgets/FoundationWidgets').then(m => m.BalanceScale), { ssr: false })
const SolveByGraph = dynamic(() => import('../_components/widgets/FoundationWidgets').then(m => m.SolveByGraph), { ssr: false })

export default function Algebra() {
  return (
    <Lesson>
      <Step>
        <H>The grammar of formulas</H>
        <P>
          Formulas are sentences made of numbers and operations, and like sentences they have grammar. The main rule: some
          operations go first. Brackets first, then powers, then × and ÷, and finally + and −.
        </P>
        <MB>{'2 + 3 \\times 4 = 2 + 12 = 14 \\qquad (2 + 3) \\times 4 = 5 \\times 4 = 20'}</MB>
        <P>
          In maths, multiplication is often written without a sign at all: <M>{'3x'}</M> means <M>{'3 \\times x'}</M>, and{' '}
          <M>{'wx + b'}</M> means <M>{'(w \\times x) + b'}</M>. A fraction bar is a division, and it acts like brackets around the
          top and the bottom.
        </P>
        <Exercise id="order-of-ops" title="Order of operations" {...EX.orderOfOps} />
      </Step>

      <Step gate="nq-ops">
        <H>Check yourself</H>
        <NumberQuiz
          id="nq-ops"
          question={
            <>
              With <M>{'w = 2'}</M>, <M>{'x = 5'}</M> and <M>{'b = -3'}</M>, what is <M>{'\\frac{wx + b}{x - 4}'}</M>?
            </>
          }
          answer={7}
          explain={
            <>
              Top: 2 × 5 + (−3) = 7. Bottom: 5 − 4 = 1. So 7 ÷ 1 = 7.
            </>
          }
          hint={<>Work out the top and the bottom separately first — the fraction bar acts like brackets.</>}
        />
      </Step>

      <Step gate="goal-balance">
        <H>An equation is a balance</H>
        <P>
          An <strong>equation</strong> says two things are equal, like <M>{'3x + 2 = 14'}</M>. Here <M>x</M> is an{' '}
          <strong>unknown</strong>: a number we haven’t found yet. <strong>Solving</strong> means finding the value that makes the
          equation true.
        </P>
        <P>
          Think of it as a balance scale. The two sides weigh the same. You can change anything you like — as long as you do
          the same to both sides, the scale stays level and the equation stays true.
        </P>
        <Goal id="goal-balance" task={<>Get one x alone on the left, with the scale still level.</>}>
          {onReach => <BalanceScale onGoal={onReach} />}
        </Goal>
        <Callout kind="plain">
          To solve, undo what was done to <M>x</M>, in reverse order. <M>{'3x + 2'}</M> means “times 3, then add 2”, so you
          take away 2 first, then divide by 3: <M>{'x = (14 - 2) \\div 3 = 4'}</M>.
        </Callout>
      </Step>

      <Step gate={['nq-solve', 'solve-linear']}>
        <H>Rearranging formulas</H>
        <P>
          The same moves let you <strong>rearrange</strong> a formula to make a different letter the subject. Start with the
          temperature formula, then undo each step:
        </P>
        <MB>{'f = 1.8\\,c + 32 \\qquad \\Rightarrow\\;\\; f - 32 = 1.8\\,c \\qquad \\Rightarrow\\;\\; c = \\frac{f - 32}{1.8}'}</MB>
        <NumberQuiz
          id="nq-solve"
          question={
            <>
              Solve <M>{'5x - 7 = 18'}</M>. What is <M>x</M>?
            </>
          }
          answer={5}
          explain={<>Add 7 to both sides: 5x = 25. Divide both sides by 5: x = 5.</>}
          hint={<>The “− 7” is undone by adding 7 to both sides.</>}
        />
        <Exercise
          id="solve-linear"
          title="solve, toCelsius, meet"
          {...EX.solveLinear}
          hints={[
            <>
              <C>return (c - b) / a</C>
            </>,
            <>
              For meet: <M>{'m_1 x + b_1 = m_2 x + b_2'}</M> becomes <M>{'(m_1 - m_2)\\,x = b_2 - b_1'}</M>. Divide by{' '}
              <M>{'m_1 - m_2'}</M>.
            </>,
          ]}
        />
      </Step>

      <Step gate="q-graph">
        <H>Solving with pictures</H>
        <P>
          Every equation is also a picture. Draw the left side as one graph and the right side as another: the solutions are
          wherever the two graphs <strong>meet</strong>. Drag <M>x</M> until the gap closes.
        </P>
        <Figure n="2.5" wide caption="Each side of the equation drawn as a graph. Where they cross, both sides are equal — that x is a solution. A curve and a line can cross twice.">
          <SolveByGraph />
        </Figure>
        <Quiz
          id="q-graph"
          question={
            <>
              How many solutions does <M>{'x^2 = x + 2'}</M> have?
            </>
          }
          options={[
            { label: 'Two: x = −1 and x = 2', correct: true },
            { label: 'One: x = 2', why: 'Drag x to the left as well — the curve and the line cross a second time.' },
            { label: 'None', why: 'Try x = 2: both sides are 4.' },
          ]}
          explain="Curved equations can have several solutions — or none at all. Training a neural network is a search through a landscape full of these."
        />
      </Step>

      <Step gate="bisection">
        <H>When you can’t rearrange: search</H>
        <P>
          Try to rearrange <M>{'x^3 + x = 10'}</M>. You can’t, not with these moves. But a computer doesn’t need to: it can{' '}
          <strong>search</strong>. Guess the middle of a range, check whether the answer is too small or too big, and throw away
          the wrong half. Sixty halvings pin the answer down to about 15 decimal places.
        </P>
        <Exercise
          id="bisection"
          title="Solve by searching"
          {...EX.bisection}
          hints={[
            <>
              <C>{'if (f(mid) < target) lo = mid'}</C> — the answer must be to the right of mid. Otherwise <C>hi = mid</C>.
            </>,
          ]}
        />
        <Callout kind="why">
          This is the big idea behind machine learning. A model with billions of parameters can’t be solved by rearranging. So
          we search — not by halving, but by following the slope downhill. You’ll learn that in Module 4.
        </Callout>
      </Step>
    </Lesson>
  )
}
